/**
 * reinitFlowbiteWidgets(root)
 *
 * Re-wire every Flowbite widget type used in this codebase, scoped to the
 * given root. The root defaults to `#main-content` (our standard HTMX swap
 * target), falling back to `#platform-content` (cleaner PWA) and finally to
 * `document` for the initial page load.
 *
 * Why this helper exists (and why you should NEVER call Flowbite's raw
 * `initFlowbite()` in this project):
 *
 * 1. Stock `initFlowbite()` scans the whole document. After an HTMX swap
 *    that only touches `#main-content`, calling it re-instantiates Popper
 *    for persistent layout widgets (topbar notification bell, language
 *    switcher, user menu). The new Popper does not inherit the open panel's
 *    inline positioning, so an already-open bell dropdown ends up floating
 *    at the top-right of the viewport instead of anchored to its icon.
 *
 * 2. Boosted navigation uses `hx-swap="outerHTML"`. HTMX removes the old
 *    `#main-content` node and inserts a fresh one. `evt.detail.target` in
 *    `htmx:afterSwap` still points at the OLD detached node — so callers
 *    MUST resolve the live element (see the afterSwap listener in app.js)
 *    before passing it here, or every widget gets wired to a ghost DOM.
 *
 * 3. Every Flowbite widget type the codebase actually uses must be covered
 *    here explicitly. Adding a new widget type to a template without
 *    teaching this function about it is guaranteed to silently break after
 *    the first HTMX navigation. Keep the list below in sync.
 *
 * Contract / when to call:
 *   - once on `DOMContentLoaded` with `document` (app.js does this)
 *   - once in the `htmx:afterSwap` listener with the live swap target
 *   - once in the `htmx:oobAfterSwap` listener with the OOB element
 *   - from any JS that injects Flowbite-markup DOM at runtime (modal
 *     helpers, drawer helpers, page forms). Always pass the newly-inserted
 *     root element explicitly.
 *
 * This function is idempotent: Flowbite's own constructors register
 * instances in a global registry with `override: true`, so re-running on
 * the same DOM replaces stale instances without leaking old event handlers.
 */

import {
    Dropdown,
    Tooltip,
    Popover,
    Modal,
    Collapse,
    Accordion,
    Datepicker,
    Drawer,
} from 'flowbite';
// Underlying vanilla datepicker class — the SAME one Flowbite's `Datepicker`
// wrapper instantiates internally (resolved to the same module by webpack).
// Its static `locales` registry is what `processOptions` reads via
// `datepicker.constructor.locales`; mutating it here is how we make
// non-English month/weekday names available. `locales` is a getter without a
// setter, so we mutate the object it returns (Object.assign) rather than
// reassigning it.
import { Datepicker as FlowbiteDatepickerBase } from 'flowbite-datepicker';

function asRoot(root) {
    if (root && root.querySelectorAll) return root;
    return (
        document.getElementById('main-content') ||
        document.getElementById('platform-content') ||
        document
    );
}

function initDropdowns(root) {
    root.querySelectorAll('[data-dropdown-toggle]').forEach(function (trigger) {
        var targetId = trigger.getAttribute('data-dropdown-toggle');
        var target = document.getElementById(targetId);
        if (!target) return;
        var skid = trigger.getAttribute('data-dropdown-offset-skidding');
        var dist = trigger.getAttribute('data-dropdown-offset-distance');
        new Dropdown(target, trigger, {
            placement: trigger.getAttribute('data-dropdown-placement') || 'bottom',
            triggerType: trigger.getAttribute('data-dropdown-trigger') || 'click',
            offsetSkidding: skid ? parseInt(skid, 10) : 0,
            offsetDistance: dist ? parseInt(dist, 10) : 10,
        });
    });
}

function initTooltips(root) {
    root.querySelectorAll('[data-tooltip-target]').forEach(function (trigger) {
        var target = document.getElementById(trigger.getAttribute('data-tooltip-target'));
        if (!target) return;
        new Tooltip(target, trigger, {
            placement: trigger.getAttribute('data-tooltip-placement') || 'top',
            triggerType: trigger.getAttribute('data-tooltip-trigger') || 'hover',
        });
    });
}

function initPopovers(root) {
    root.querySelectorAll('[data-popover-target]').forEach(function (trigger) {
        var target = document.getElementById(trigger.getAttribute('data-popover-target'));
        if (!target) return;
        new Popover(target, trigger, {
            placement: trigger.getAttribute('data-popover-placement') || 'top',
            triggerType: trigger.getAttribute('data-popover-trigger') || 'hover',
        });
    });
}

// Mirrors flowbite's own `initModals()` (lib/esm/components/modal/index.js):
// two passes — (1) build a Modal instance per `[data-modal-target]`, then
// (2) wire click handlers on every `[data-modal-toggle]`, `[data-modal-show]`
// and `[data-modal-hide]` trigger. The stock library only runs that second
// pass on `window.load`, which never fires during HTMX boosted navigation.
// Without pass #2 here, any modal opened via a toggle button silently stops
// working after the first HTMX swap (fresh button DOM, no listener) and only
// comes back after a full page refresh. Noticed first on the "New mail"
// button of /office/emails/.
function initModals(root) {
    root.querySelectorAll('[data-modal-target]').forEach(function (trigger) {
        var targetId = trigger.getAttribute('data-modal-target');
        var target = document.getElementById(targetId);
        if (!target) return;
        new Modal(target, {
            placement: trigger.getAttribute('data-modal-placement') || 'center',
            backdrop: trigger.getAttribute('data-modal-backdrop') || 'dynamic',
        });
    });

    wireModalTriggers(root, 'data-modal-toggle', 'toggle');
    wireModalTriggers(root, 'data-modal-show', 'show');
    wireModalTriggers(root, 'data-modal-hide', 'hide');
}

// Attach a click listener that calls `method` on the Modal instance whose id
// matches the attribute value. Guarded by `dataset.modalWired` so re-running
// the helper after another HTMX swap never stacks duplicate listeners on the
// same button (same idempotency pattern as wireDrawerTriggers below).
function wireModalTriggers(root, attr, method) {
    root.querySelectorAll('[' + attr + ']').forEach(function (trigger) {
        if (trigger.dataset.modalWired === attr) return;
        var modalId = trigger.getAttribute(attr);
        var modalEl = document.getElementById(modalId);
        if (!modalEl) return;
        trigger.addEventListener('click', function () {
            var inst = window.FlowbiteInstances
                ? window.FlowbiteInstances.getInstance('Modal', modalId)
                : null;
            if (inst && typeof inst[method] === 'function') inst[method]();
        });
        trigger.dataset.modalWired = attr;
    });
}

// Sidebar submenus (Cleaners / Billing / Stock / Emails) and any other
// `data-collapse-toggle` pairs. The sidebar is OOB-swapped on every boosted
// navigation, so the fresh <aside> always needs fresh Collapse instances.
function initCollapses(root) {
    root.querySelectorAll('[data-collapse-toggle]').forEach(function (trigger) {
        var target = document.getElementById(trigger.getAttribute('data-collapse-toggle'));
        if (!target) return;
        new Collapse(target, trigger);
    });
}

// Accordion-based expandable table rows (consumables, availability,
// audit-log) and the public FAQ.
function initAccordions(root) {
    root.querySelectorAll('[data-accordion]').forEach(function (accordionEl) {
        var alwaysOpen = accordionEl.getAttribute('data-accordion');
        var items = [];
        accordionEl.querySelectorAll('[data-accordion-target]').forEach(function (triggerEl) {
            if (triggerEl.closest('[data-accordion]') !== accordionEl) return;
            var targetSel = triggerEl.getAttribute('data-accordion-target');
            items.push({
                id: targetSel,
                triggerEl: triggerEl,
                targetEl: document.querySelector(targetSel),
                iconEl: triggerEl.querySelector('[data-accordion-icon]'),
                active: triggerEl.getAttribute('aria-expanded') === 'true',
            });
        });
        new Accordion(accordionEl, items, { alwaysOpen: alwaysOpen === 'open' });
    });
}

// Inline (×) clear button for single-input datepickers.
//
// The office asked for a consistent "wipe this date" affordance on EVERY
// date field (filters, billing, forms) — mirroring the customer picker's
// (×). Rather than copy the markup into ~25 templates, we enhance every
// ``input[datepicker]`` here, where Flowbite datepickers are already
// (re)wired on each load / HTMX swap / OOB swap.
//
// Idempotent via a per-element JS-property flag (``_clearBtnAdded``): the
// input DOM node is swapped out on every boosted navigation, so the guard
// MUST live on the element, never at module level (see
// .cursor/rules/hx-boost-init.mdc rule 4). The click/visibility listeners
// attach to the input + injected button, both inside the swapped subtree,
// so they are garbage-collected with it — no leak across navigations.
var DATEPICKER_CLEAR_BTN_CLASS =
    'datepicker-clear-btn hidden absolute inset-y-0 right-2 flex items-center ' +
    'justify-center w-6 h-6 my-auto rounded-full text-gray-400 hover:text-gray-600 ' +
    'hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 ' +
    'dark:hover:bg-gray-700 focus:outline-none';

var DATEPICKER_CLEAR_SVG =
    '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">' +
    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" ' +
    'd="M6 18L18 6M6 6l12 12"></path></svg>';

// aria-label, surfaced from the office layout's json_script so it follows
// the user's locale (no hardcoded user-visible string in JS — cf.
// .cursor/rules/i18n-english-msgids.mdc rule 5). English fallback for
// shells that don't render the block (e.g. cleaner PWA).
var _datepickerClearLabel = null;
function getDatepickerClearLabel() {
    if (_datepickerClearLabel !== null) return _datepickerClearLabel;
    _datepickerClearLabel = 'Clear date';
    var node = document.getElementById('datepicker-i18n');
    if (node) {
        try {
            var data = JSON.parse(node.textContent);
            if (data && data.clear) _datepickerClearLabel = data.clear;
        } catch (e) {
            /* keep English fallback */
        }
    }
    return _datepickerClearLabel;
}

function addDatepickerClearButton(input) {
    // Single text inputs only — skip inline calendars and range-picker
    // containers (both are <div>/dual-input and unused in the office today).
    if (input.tagName !== 'INPUT' || input._clearBtnAdded) return;
    if (input.hasAttribute('inline-datepicker') || input.hasAttribute('date-rangepicker')) return;

    var parent = input.parentElement;
    if (!parent) return;
    input._clearBtnAdded = true;

    // The button is absolutely positioned against the input's wrapper. Every
    // office date field wraps the input in a ``.relative`` div (left calendar
    // icon + full-width input); guarantee positioning if a one-off field
    // forgot the class so the (×) never escapes to the page corner.
    if (window.getComputedStyle(parent).position === 'static') {
        parent.classList.add('relative');
    }
    // ``form-input-icon`` ships ``pr-2.5``; widen it so the value never runs
    // under the (×).
    input.classList.add('pr-9');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = DATEPICKER_CLEAR_BTN_CLASS;
    btn.setAttribute('aria-label', getDatepickerClearLabel());
    btn.innerHTML = DATEPICKER_CLEAR_SVG;
    input.insertAdjacentElement('afterend', btn);

    function toggle() {
        btn.classList.toggle('hidden', !input.value);
    }
    // Flowbite emits ``changeDate`` on pick; ``input``/``change`` cover manual
    // typing, native autofill and our own programmatic clear.
    ['input', 'change', 'changeDate'].forEach(function (evt) {
        input.addEventListener(evt, toggle);
    });
    btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        // Clear through the picker's own API so its internal selection is
        // wiped too (it fires ``changeDate`` natively). A manual
        // ``changeDate`` dispatch is wrong: flowbite-datepicker re-syncs the
        // input FROM its still-selected date, silently restoring the value.
        if (input.datepicker && typeof input.datepicker.setDate === 'function') {
            input.datepicker.setDate({ clear: true });
        }
        // Belt-and-suspenders for a value set programmatically after the
        // picker was constructed (its internal ``dates`` never saw it).
        input.value = '';
        // ``change`` drives the filter forms that auto-submit on date change.
        input.dispatchEvent(new Event('change', { bubbles: true }));
        toggle();
    });
    toggle();
}

// Locale tables for the languages CleanTask ships (EN ships with the lib).
// Copied from flowbite-datepicker's i18n source — the package does not expose
// its locale files via `exports`, and its `./locales` bundle is not shipped,
// so importing them at runtime is impossible. Only month/weekday names and the
// week-start day are localised; the input format is always supplied explicitly
// by the wrapper (Default.format) so submitted values keep their numeric
// mm/dd/yyyy shape regardless of language.
var DATEPICKER_LOCALES = {
    fr: {
        days: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
        daysShort: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
        daysMin: ['d', 'l', 'ma', 'me', 'j', 'v', 's'],
        months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
        monthsShort: ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
        today: "Aujourd'hui",
        monthsTitle: 'Mois',
        clear: 'Effacer',
        weekStart: 1,
        format: 'dd/mm/yyyy',
    },
    nl: {
        days: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
        daysShort: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
        daysMin: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
        months: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
        monthsShort: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
        today: 'Vandaag',
        monthsTitle: 'Maanden',
        clear: 'Wissen',
        weekStart: 1,
        format: 'dd-mm-yyyy',
    },
    pt: {
        days: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
        daysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        daysMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
        months: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
        monthsShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        today: 'Hoje',
        monthsTitle: 'Meses',
        clear: 'Limpar',
        format: 'dd/mm/yyyy',
    },
    'pt-BR': {
        days: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
        daysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        daysMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
        months: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
        monthsShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        today: 'Hoje',
        monthsTitle: 'Meses',
        clear: 'Limpar',
        format: 'dd/mm/yyyy',
    },
};

var _datepickerLocalesRegistered = false;
export function registerDatepickerLocales() {
    if (_datepickerLocalesRegistered) return;
    _datepickerLocalesRegistered = true;
    try {
        // `locales` is a getter returning the live registry object; mutating it
        // adds our languages without needing a (non-existent) setter.
        if (FlowbiteDatepickerBase && FlowbiteDatepickerBase.locales) {
            Object.assign(FlowbiteDatepickerBase.locales, DATEPICKER_LOCALES);
        }
    } catch (err) {
        // Non-fatal: datepickers simply stay in English.
        if (window.console) console.warn('[datepicker] locale registration failed', err);
    }
}

// Maps Django's active LANGUAGE_CODE (from <html lang>) to a registered
// datepicker locale key. flowbite-datepicker only does a coarse `xx-YY → xx`
// fallback, so we normalise 'pt-br' to the exact 'pt-BR' key ourselves to keep
// the Brazilian variant instead of collapsing it to Portugal Portuguese.
export function activeDatepickerLanguage() {
    var raw = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
    if (raw === 'pt-br' || raw === 'pt_br') return 'pt-BR';
    var base = raw.split('-')[0];
    if (base === 'pt') return 'pt';
    if (base === 'fr' || base === 'nl') return base;
    return 'en';
}

// Flowbite (imported via 'flowbite') registers its own ``window 'load'``
// datepicker auto-init at module-eval time. That stock pass defaults to
// English (it only reads an explicit ``datepicker-language`` attribute, which
// our templates don't set) and runs AFTER our DOMContentLoaded init — so on
// every FULL page load it silently clobbers our correctly-localised pickers
// with English ones. Boosted navigations don't fire ``load``, so only our pass
// runs there, which is why the bug looked asymmetric (correct after boosted
// nav, English after a hard load / language switch). Registering our own
// ``load`` listener from inside an init call (i.e. after Flowbite's listener
// is already on the queue, but before ``load`` fires) guarantees we run last
// and restore the active language.
var _dpLoadReconcileBound = false;
function bindDatepickerLoadReconcileOnce() {
    if (_dpLoadReconcileBound) return;
    _dpLoadReconcileBound = true;
    if (typeof window === 'undefined') return;
    window.addEventListener('load', function () {
        // Defer to a macrotask so we always run AFTER Flowbite's synchronous
        // load-time auto-init, regardless of listener registration order.
        setTimeout(function () {
            var scope = asRoot(document);
            if (scope) initDatepickers(scope);
        }, 0);
    });
}

function initDatepickers(root) {
    bindDatepickerLoadReconcileOnce();
    registerDatepickerLocales();
    var pageLanguage = activeDatepickerLanguage();
    root.querySelectorAll('[datepicker], [inline-datepicker], [date-rangepicker]').forEach(function (el) {
        // Idempotent; only acts on single-input datepickers.
        addDatepickerClearButton(el);
        var language = el.getAttribute('datepicker-language');
        var wantLang = language || pageLanguage;
        // schedule-team.js owns its jump picker via ``el._datepicker`` and
        // already constructs it with the active language — leave it alone.
        if (el._datepicker) return;
        var existing = el.datepicker; // underlying Datepicker instance, if any
        if (existing) {
            var curLang = existing.config && existing.config.language;
            // Wrong language (typically Flowbite's stock load-time auto-init,
            // which defaults to English). Switch the locale in place — the
            // underlying ``setOptions`` runs the language→locale resolution and
            // re-renders the calendar, so there's no popup-DOM churn.
            if (curLang !== wantLang && typeof existing.setOptions === 'function') {
                try {
                    existing.setOptions({ language: wantLang });
                } catch (err) {
                    if (window.console) console.warn('[datepicker] could not switch locale', err);
                }
            }
            return;
        }
        var opts = { rangePicker: el.hasAttribute('date-rangepicker') };
        if (el.hasAttribute('datepicker-buttons')) opts.buttons = true;
        if (el.hasAttribute('datepicker-autoselect-today')) opts.autoSelectToday = true;
        if (el.hasAttribute('datepicker-autohide')) opts.autohide = true;
        var format = el.getAttribute('datepicker-format');
        var maxDate = el.getAttribute('datepicker-max-date');
        var minDate = el.getAttribute('datepicker-min-date');
        var orientation = el.getAttribute('datepicker-orientation');
        var title = el.getAttribute('datepicker-title');
        if (format) opts.format = format;
        if (maxDate) opts.maxDate = maxDate;
        if (minDate) opts.minDate = minDate;
        if (orientation) opts.orientation = orientation;
        if (title) opts.title = title;
        // Default every datepicker to the active UI language so month/weekday
        // names match the page; an explicit ``datepicker-language`` attribute
        // still wins when a template needs to pin a specific locale.
        opts.language = wantLang;
        new Datepicker(el, opts);
    });
}

// Drawers are used by the topbar hamburger on mobile to open the sidebar.
// The topbar is never swapped, so in practice this only runs on the initial
// page load. Keeping it here means the helper is a true single source of
// truth — any future drawer usage inside #main-content will Just Work.
function initDrawers(root) {
    root.querySelectorAll('[data-drawer-target]').forEach(function (trigger) {
        var drawerId = trigger.getAttribute('data-drawer-target');
        var drawerEl = document.getElementById(drawerId);
        if (!drawerEl) return;
        var placement = trigger.getAttribute('data-drawer-placement');
        var bodyScrolling = trigger.getAttribute('data-drawer-body-scrolling');
        var backdrop = trigger.getAttribute('data-drawer-backdrop');
        var edge = trigger.getAttribute('data-drawer-edge');
        var edgeOffset = trigger.getAttribute('data-drawer-edge-offset');
        new Drawer(drawerEl, {
            placement: placement || 'left',
            bodyScrolling: bodyScrolling === 'true',
            backdrop: backdrop ? backdrop === 'true' : true,
            edge: edge === 'true',
            edgeOffset: edgeOffset || '',
        });
    });
    wireDrawerTriggers(root, 'data-drawer-toggle', 'toggle');
    wireDrawerTriggers(root, 'data-drawer-show', 'show');
    wireDrawerTriggers(root, 'data-drawer-dismiss', 'hide');
    wireDrawerTriggers(root, 'data-drawer-hide', 'hide');
}

function wireDrawerTriggers(root, attr, method) {
    root.querySelectorAll('[' + attr + ']').forEach(function (trigger) {
        if (trigger.dataset.drawerWired === attr) return;
        var drawerId = trigger.getAttribute(attr);
        var drawerEl = document.getElementById(drawerId);
        if (!drawerEl) return;
        // Flowbite stores Drawer instances in a global registry keyed by id.
        // We rely on that here instead of re-importing FlowbiteInstances.
        var handler = function () {
            var inst = window.FlowbiteInstances
                ? window.FlowbiteInstances.getInstance('Drawer', drawerId)
                : null;
            if (inst && typeof inst[method] === 'function') inst[method]();
        };
        trigger.addEventListener('click', handler);
        trigger.dataset.drawerWired = attr;
    });
}

export function reinitFlowbiteWidgets(root) {
    var scope = asRoot(root);
    if (!scope) return;
    initDropdowns(scope);
    initTooltips(scope);
    initPopovers(scope);
    initModals(scope);
    initCollapses(scope);
    initAccordions(scope);
    initDatepickers(scope);
    initDrawers(scope);
}

// Expose globally so inline template scripts can call it. Keeping this
// explicit and out of the Flowbite namespace (no `window.initFlowbite`
// override) preserves a clean boundary between "what the library ships"
// and "how this project chooses to use it".
window.reinitFlowbiteWidgets = reinitFlowbiteWidgets;
